import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const scopes = [
  { icon: "ri-newspaper-line", title: "动漫资讯", desc: "新番定档、剧场版、行业报告与展会活动的第一手动态。" },
  { icon: "ri-movie-2-line", title: "作品推荐", desc: "动画与漫画精选，覆盖科幻、热血、治愈、悬疑等多种类型。" },
  { icon: "ri-search-eye-line", title: "剧情解析", desc: "伏笔梳理、结局解读、主题探讨与创作技巧的深度文章。" },
  { icon: "ri-book-open-line", title: "动漫文化", desc: "发展历史、名家介绍、声优与周边文化的专题合集。" },
];

const contacts = [
  { icon: "ri-mail-line", label: "内容合作与投稿", value: "contact@staranime.example" },
  { icon: "ri-customer-service-2-line", label: "问题反馈", value: "support@staranime.example" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "星空动漫" },
  { icon: "ri-weibo-line", label: "官方微博", value: "@星空动漫" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 星空动漫",
    description:
      "星空动漫是面向动漫爱好者与行业关注者的专业动漫内容门户，提供资讯、作品推荐、剧情解析与文化专题。了解我们的定位、内容范围与联系方式。",
    keywords: "关于星空动漫,联系我们,内容范围,版权说明,反馈渠道",
  });

  return (
    <SiteLayout>
      <div className="mx-auto max-w-page px-4 py-8 md:px-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <section className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-primary-50 p-6 md:p-10">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
            <i className="ri-planet-line" />
            关于星空动漫
          </span>
          <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-4xl">
            在星空下，陪你读懂每一部好作品
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
            星空动漫是一个专业的动漫内容门户网站，面向广大动漫爱好者与行业关注者。我们的目标很简单：把繁杂的动漫信息整理清楚，让你在有限的时间里，快速获取准确的资讯、找到值得一看的作品，并真正读懂故事背后的设计与文化。
          </p>
        </section>

        <section className="mt-10">
          <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">内容范围</h2>
          <p className="mt-2 text-sm text-foreground-600">
            网站内容围绕四个频道展开，彼此关联、互相推荐。
          </p>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {scopes.map((scope) => (
              <div
                key={scope.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${scope.icon} text-2xl`} />
                </span>
                <h3 className="text-base font-semibold text-foreground-950">{scope.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{scope.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-contacts-line text-accent-500" />
              联系方式
            </h2>
            <ul className="mt-4 flex flex-col gap-4">
              {contacts.map((contact) => (
                <li key={contact.label} className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${contact.icon} text-xl`} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{contact.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{contact.value}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <i className="ri-shield-check-line text-accent-500" />
                版权信息说明
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                本站为动漫内容演示站点，页面中所展示的作品名称、资讯文本与图片均为示例内容，仅用于展示产品形态与信息组织方式，不代表真实作品动态。本站不提供任何影视资源的下载或在线播放，所有内容仅用于资讯分享与文化交流。如涉及版权问题，请通过上方邮箱与我们联系。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <i className="ri-book-read-line text-accent-500" />
                使用须知与反馈渠道
              </h2>
              <ul className="mt-3 flex flex-col gap-2.5 text-sm leading-relaxed text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                  站内搜索、分类筛选与收藏功能均为前端演示，收藏状态仅保存在你的浏览器本地。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                  页面内容为示例数据，用于展示资讯与作品的组织方式，不构成任何作品评价。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                  如发现内容错误或有合作意向，欢迎通过 support@staranime.example 反馈。
                </li>
              </ul>
              <Link
                to="/search"
                className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                去站内搜索看看
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}